import { z } from 'zod';

/**
 * Одна таблица кодов ошибок (contracts/http-api.md «Коды ошибок», step-contract.md §4,
 * rules-service.md, mcp.md, data-model.md А.5; коды 001 — specs/001 contracts/http-api.md;
 * коды 003 — specs/003 contracts/http-api.md, «Новые коды ошибок»; коды 004 — specs/004 contracts/http-api.md,
 * «Коды ошибок (новые…)»).
 *
 * Вид кода:
 * - `response` — ответ с HTTP-статусом (в MCP часть из них — ошибка инструмента при HTTP 200);
 * - `check` — «только в списке проверки»: своего HTTP-статуса нет, приходит списком в ответе
 *   записи черновика (200) и в `details.errors` у `409 VALIDATION_FAILED`;
 * - `calc` — ошибка расчёта дня (`CalcError`): ячейка пустая, `error: { code }`, пометка
 *   `ядро.ошибка_расчёта`. HTTP-статуса нет.
 */
export type ErrorSpec =
  | { readonly kind: 'response'; readonly http: HttpErrorStatus; readonly listOnly: false }
  | { readonly kind: 'check'; readonly http: null; readonly listOnly: true }
  | { readonly kind: 'calc'; readonly http: null; readonly listOnly: false };

export type HttpErrorStatus = 400 | 401 | 403 | 404 | 409 | 410 | 413 | 429 | 500 | 503;

const http = <S extends HttpErrorStatus>(status: S) =>
  ({ kind: 'response', http: status, listOnly: false }) as const;
const check = { kind: 'check', http: null, listOnly: true } as const;
const calc = { kind: 'calc', http: null, listOnly: false } as const;

export const ERRORS = {
  // 001 — коды остаются, статусы прежние.
  VALIDATION: http(400),
  PERIOD_INVALID: http(400),
  PERIOD_TOO_LONG: http(400),
  HOURS_INVALID: http(400),
  CELL_COMMENT_REQUIRED: http(400),
  PERSON_NOT_IN_PERIOD: http(400),
  UNAUTHENTICATED: http(401),
  BAD_CREDENTIALS: http(401),
  /** Нет пары роль + объект; на маршрутах 001 — и отказ по действию, как в 001 (Р-58). */
  ROLE_FORBIDDEN: http(403),
  ORIGIN_FORBIDDEN: http(403),
  NOT_FOUND: http(404),
  STATUS_FORBIDS_ACTION: http(409),
  FUTURE_DAY: http(409),
  TIMESHEET_SIGNED: http(409),
  LOGIN_LOCKED: http(429),
  INTERNAL: http(500),

  // 002, 400.
  /** Неизвестный кубик или функция ядра: отказ записи черновика. */
  UNKNOWN_CUBE: http(400),
  /** Режим или значение волны 2: отказ записи. */
  WAVE2_MODE: http(400),
  BASIS_REQUIRED: http(400),
  REASON_REQUIRED: http(400),
  COMMENT_REQUIRED: http(400),
  /** Правка без строки Табеля, а на дате несколько строк (Р-40). */
  ROW_REQUIRED: http(400),

  // 002, 403.
  /** Действие не разрешено роли (новые маршруты 002, сервис правил). */
  FORBIDDEN: http(403),
  /** Изменение значения или режима закона, выключение кубика закона: отказ записи. */
  LAW_LOCKED: http(403),
  /** Действие или вариант входа запрещены в канале. */
  FORBIDDEN_CHANNEL: http(403),
  SECOND_APPROVER_REQUIRED: http(403),
  AGENT_DATA_DISABLED: http(403),
  /** Только `/mcp`: нет права токена, `WWW-Authenticate: insufficient_scope`. */
  INSUFFICIENT_SCOPE: http(403),

  // 002, 409.
  DRAFT_CHANGED: http(409),
  DRAFT_NOT_EDITABLE: http(409),
  NEEDS_REBUILD: http(409),
  PREVIEW_STALE: http(409),
  GOLDEN_FAILED: http(409),
  VALIDATION_FAILED: http(409),
  /** Отказ отката: реализаций версии нет в сборке. Не код проверки. */
  IMPL_GONE: http(409),
  SIGN_BLOCKED: http(409),
  PERSON_DAY_SIGNED: http(409),

  // 002, 410 и 429.
  LINK_EXPIRED: http(410),
  RATE_LIMITED: http(429),

  // 003 (вопросы бригадиру, отмена в тосте, путь бригадира).
  /** Время ответа не разобрано («19:00», «1900», «19»). */
  TIME_INVALID: http(400),
  /** Время выхода раньше входа и дальше 16 ч от него. */
  EXIT_BEFORE_ENTRY: http(400),
  /** Вопроса нет в выводе: снят пересчётом, человек убран из табеля, день закрыт документом. */
  QUESTION_GONE: http(409),
  /** Состояние изменилось после загрузки формы: в `details.current` — текущее. */
  STALE_STATE: http(409),
  /** Правило объекта запрещает сдать табель с вопросами без ответа. */
  QUESTIONS_OPEN: http(409),
  /** Период пересекается с табелем той же бригады, создание — только с подтверждением. */
  PERIOD_OVERLAP: http(409),
  /** Отменить можно только своё последнее действие в табеле. */
  UNDO_NOT_LAST: http(409),
  /** После действия сущность изменилась или сменился статус табеля. */
  UNDO_STALE: http(409),
  /** Время на отмену прошло. */
  UNDO_EXPIRED: http(409),

  // 004 (редактор правил: черновик с ведущим, публикация автором, возврат, «Не вводить», пересчёт).
  /** Дата начала раньше даты начала последней опубликованной версии; в `details.earliest` — самая ранняя. */
  VALID_FROM_TOO_EARLY: http(400),
  /** Черновик объекта ведёт другой администратор правил; в `details.leaderName` — кто. */
  DRAFT_LED_BY_OTHER: http(409),
  /** Возврат или «Не вводить», пока на объекте есть черновик. */
  DRAFT_OPEN: http(409),
  /** В черновике нет изменений. */
  NOTHING_TO_PUBLISH: http(409),
  /** Есть риск без отметки «Понимаю риск»; в `details.codes` — коды рисков. */
  RISK_NOT_ACKNOWLEDGED: http(409),
  /** Отпечаток риска в отметке не совпал с текущим: значения изменились. */
  RISK_CHANGED: http(409),
  /** Черновик даёт ошибку расчёта в днях влияния; в `details.days` — какие. */
  CALC_ERRORS: http(409),
  /** Расчёт влияния снят более новым запросом того же пользователя. */
  IMPACT_SUPERSEDED: http(409),
  /** Подпись табеля, пока его дни пересчитываются по новым правилам. */
  RECALC_IN_PROGRESS: http(409),
  /** «Не вводить» у версии, которая уже начала действовать. */
  VERSION_NOT_FUTURE: http(409),
  /** «Вернуть» у версии не в статусе «действовала». */
  VERSION_NOT_PAST: http(409),
  /** Сбой расчёта примера или влияния; код ошибки — `requestId`. */
  CALC_FAILED: http(500),

  // 005 (MCP-сервер правил и скилл; contracts/mcp-tools.md «Ошибки»). В MCP — ошибка инструмента (`isError`).
  /** У пользователя нет права «ИИ-агент» (FR-063). */
  AGENT_RIGHT_MISSING: http(403),
  /** Изменяющий вызов при другой версии скилла (FR-045). */
  SKILL_VERSION_MISMATCH: http(409),
  /** У черновика есть находки «блокирует»; план не замораживается. */
  PLAN_BLOCKED: http(409),
  /** План устарел; в `details.reason` — что изменилось. */
  PLAN_STALE: http(409),
  /** Выборки дней не разрешены для этого клиента агента (FR-028). */
  CLIENT_NOT_ALLOWED: http(403),
  /** В тексте ФИО или номер человека (FR-036, канал `mcp`). */
  PERSONAL_DATA: http(400),
  /** На сервере выключен расчёт по правилам. */
  RULES_UNAVAILABLE: http(409),

  // 006 (клиент ИИ-агента; specs/006-agent-client/contracts/http-api.md, connector-protocol.md).
  /** Ключ подключения компьютера неверный или просрочен (US1-3). */
  ENROLLMENT_INVALID: http(401),
  /** Ключ подключения уже использован: он одноразовый (US1-2). */
  ENROLLMENT_USED: http(409),
  /** Ключ компьютера отозван: компьютер отключён (FR-004). */
  CONNECTOR_REVOKED: http(401),
  /** Компьютер не в сети: команда не дошла. */
  CONNECTOR_OFFLINE: http(409),
  /** Агент на компьютере не готов: в `details.state` — состояние FR-008. */
  AGENT_NOT_READY: http(409),
  /** На компьютере уже идёт установка (FR-009). */
  INSTALL_RUNNING: http(409),
  /** Запрос агента уже не ждёт ответа (FR-027). */
  INTERACTION_CLOSED: http(409),
  /** Агент сейчас не работает: останавливать нечего. */
  TURN_NOT_RUNNING: http(409),
  /** Разговор в архиве: писать в него нельзя, пока он не возвращён из архива (contracts/ui.md §3). */
  THREAD_ARCHIVED: http(409),
  // 007 (площадки; specs/007-sites contracts/http-api.md).
  /** Площадка с таким названием уже есть. */
  SITE_NAME_TAKEN: http(409),
  /** Код площадки занят другим объектом. */
  SITE_CODE_TAKEN: http(409),
  /** Город не из списка, а пояс не указан. */
  CITY_UNKNOWN: http(400),
  /** Секрет площадки больше не показывается: шаг выполнен. Нужно «Выдать новый». */
  SECRET_EXPIRED: http(410),
  /** Адреса нет среди последних отказов входа площадки. */
  ADDRESS_NOT_SEEN: http(409),
  /** Не готовы шаги, нужные для действия; номера — в `details.steps`. */
  STEPS_NOT_READY: http(409),
  /** Площадку изменили в другом окне; ревизия устарела. */
  SITE_REVISION_CONFLICT: http(409),
  /** Введённое название не совпадает с названием площадки. */
  CONFIRM_NAME_MISMATCH: http(400),
  /** Файл выгрузки не разобран; причины — в `details`. */
  UPLOAD_INVALID: http(400),
  /** Файл выгрузки больше 20 МБ или 200 000 строк. */
  UPLOAD_TOO_LARGE: http(413),
  /** «Проверить снова» чаще раза в 30 секунд. */
  WATCH_TOO_OFTEN: http(429),
  /** Продолжить с риском можно только у шага с предупреждением. */
  RISK_NOT_ALLOWED: http(409),
  /** Шаг 7 вручную — только после двух утр без строк выгрузки (уточнение 26.10.2026). */
  EXTRACT_CONFIRM_NOT_ALLOWED: http(409),
  /** Исполнитель подключения на сервере недоступен. */
  OPS_UNAVAILABLE: http(503),
  /** Площадка не запущена, правила к запуску не готовили: правил у неё нет (FR-004b, поправка 20.10.2026). */
  RULES_NOT_PREPARED: http(404),
  /** Дата раньше первой версии правил объекта; в сообщении — дата начала первой версии (FR-004b). */
  RULES_NOT_ON_DATE: http(404),
  /** Правила к запуску начинают действовать только при запуске площадки (шаг 10): публикации и возврата нет (FR-004a). */
  LAUNCH_RULES: http(409),

  // Только в списке проверки (step-contract.md §4).
  SCHEMA: check,
  NO_DEFAULT_REQUIRED: check,
  BUNDLE_SPLIT: check,
  DUPLICATE_BLOCK: check,
  IMPL_MISSING: check,
  IMPL_ENGINE_MISMATCH: check,
  Q1_LUNCH_DOUBLE: check,
  Q1_LUNCH_FIRSTLAST_GAP: check,
  Q1_LUNCH_PLACE_UNSET: check,
  Q1_LUNCH_BUILT_UNSET: check,
  Q1_B6_SPLIT: check,
  Q1_MAXLEN_VS_PLAN: check,
  Q1_O_BUNDLE: check,
  Q1_WINDOW_AFTER_SHORT: check,
  Q1_G2_WITHOUT_P3: check,
  Q1_O1_WITHOUT_G3_THRESHOLD: check,
  /** E0a: вопрос бригадиру у пометки «инфо» или «выкл» (specs/003 contracts/rules-params.md). */
  QUESTION_ON_INFO: check,
  /** E0a: код в `вопрос_бригадиру` не из `flags` включённых кубиков и не `сервис.две_бригады`. */
  UNKNOWN_FLAG: check,

  // Ошибки расчёта дня (data-model.md А.5).
  INV_PASS_MUTATED: calc,
  INV_MIDNIGHT: calc,
  INV_COVERAGE: calc,
  INV_DAY_24H: calc,
  INV_CHECKSUM: calc,
  INV_DOC_PLAN: calc,
  INV_TABEL_LINE: calc,
  OP_UNDECLARED: calc,
  STEP_THROWN: calc,
  INPUT_INCOMPLETE: calc,
} as const satisfies Record<string, ErrorSpec>;

type Table = typeof ERRORS;
export type ErrorCode = keyof Table;
/** Коды ответа с HTTP-статусом — только они идут в `ErrorBody` и `AppError`. */
export type ResponseErrorCode = {
  [C in ErrorCode]: Table[C]['kind'] extends 'response' ? C : never;
}[ErrorCode];
/** Коды «только в списке проверки». */
export type CheckCode = { [C in ErrorCode]: Table[C]['kind'] extends 'check' ? C : never }[ErrorCode];
/** Коды ошибок расчёта дня (`CalcError`). */
export type CalcErrorCode = { [C in ErrorCode]: Table[C]['kind'] extends 'calc' ? C : never }[ErrorCode];

const codesOf = <C extends ErrorCode>(kind: ErrorSpec['kind']) =>
  (Object.keys(ERRORS) as ErrorCode[]).filter((c) => ERRORS[c].kind === kind) as C[];

export const ERROR_CODES = Object.keys(ERRORS) as [ErrorCode, ...ErrorCode[]];

/**
 * Коды остановки сервера при старте (004, research R1): ответа HTTP у них нет, сервер не стартует, в журнале — `error`
 * с кодом. `RULES_IMPORT_ID_MISMATCH` — номера версий в базе не совпали с файлом при переносе; `RULESET_INVALID_ON_START` —
 * введённая версия из базы не проходит проверку набора после выпуска движка.
 */
export const STARTUP_ERROR_CODES = ['RULES_IMPORT_ID_MISMATCH', 'RULESET_INVALID_ON_START'] as const;
export type StartupErrorCode = (typeof STARTUP_ERROR_CODES)[number];
export const RESPONSE_ERROR_CODES = codesOf<ResponseErrorCode>('response') as [
  ResponseErrorCode,
  ...ResponseErrorCode[],
];
export const CHECK_CODES = codesOf<CheckCode>('check') as readonly CheckCode[];
export const CALC_ERROR_CODES = codesOf<CalcErrorCode>('calc') as readonly CalcErrorCode[];

/** HTTP-статус кода; `null` — у кодов списка проверки и ошибок расчёта. */
export function httpStatusOf<C extends ErrorCode>(code: C): Table[C]['http'] {
  return ERRORS[code].http;
}

/** Признак «только в списке проверки»: запись черновика такой код не отклоняет. */
export function isListOnly(code: ErrorCode): boolean {
  return ERRORS[code].listOnly;
}

/**
 * Строка списка проверки (step-contract §4, rules-service «Проверка тела черновика»): `{ code, cube, field, message }`.
 * У ошибок тела черновика кубика нет, у ошибок набора может не быть поля — `cube` и `field` необязательны.
 */
export const CheckItem = z.object({
  code: z.enum(ERROR_CODES),
  cube: z.string().optional(),
  field: z.string().optional(),
  message: z.string(),
});
export type CheckItem = z.infer<typeof CheckItem>;

const RetryAfter = z.object({ retryAfterSec: z.number().int().positive() });

/**
 * `details` тела ошибки по коду (Р-91). У этих кодов `details` обязательны, у остальных их нет.
 * - `FORBIDDEN` — причина отказа (`contracts/http-api.md`, «Общие правила»);
 * - `DRAFT_CHANGED` — отличия черновика; форма отличий — у сервиса правил;
 * - `VALIDATION_FAILED` — список проверки;
 * - `SIGN_BLOCKED` — блокирующие пометки: код и число (FR-043);
 * - `RATE_LIMITED`, `LOGIN_LOCKED` — через сколько секунд повторить;
 * - `STALE_STATE` — текущее состояние (вопроса или дня) для показа рядом с введённым (003);
 * - `QUESTIONS_OPEN` — число вопросов без ответа (003);
 * - `PERIOD_OVERLAP` — табели той же бригады, с которыми пересекается период (003);
 * - `DRAFT_LED_BY_OTHER` — кто ведёт черновик (004);
 * - `RISK_NOT_ACKNOWLEDGED` — коды рисков без отметки (004);
 * - `VALID_FROM_TOO_EARLY` — самая ранняя допустимая дата начала (004);
 * - `CALC_ERRORS` — дни влияния с ошибкой расчёта (004).
 * - `PLAN_STALE` — что изменилось после заморозки плана (005).
 */
export const ERROR_DETAILS = {
  FORBIDDEN: z.object({ reason: z.string() }),
  DRAFT_CHANGED: z.object({ diff: z.unknown() }),
  VALIDATION_FAILED: z.object({ errors: z.array(CheckItem) }),
  SIGN_BLOCKED: z.object({
    reasons: z.array(z.object({ code: z.string(), count: z.number().int().positive() })),
  }),
  RATE_LIMITED: RetryAfter,
  LOGIN_LOCKED: RetryAfter,
  STALE_STATE: z.object({ current: z.unknown() }),
  QUESTIONS_OPEN: z.object({ count: z.number().int().positive() }),
  PERIOD_OVERLAP: z.object({
    overlaps: z.array(
      z.object({
        id: z.number().int(),
        name: z.string(),
        periodStart: z.string(),
        periodEnd: z.string(),
        status: z.string(),
      }),
    ),
  }),
  DRAFT_LED_BY_OTHER: z.object({ leaderName: z.string() }),
  RISK_NOT_ACKNOWLEDGED: z.object({ codes: z.array(z.string()).min(1) }),
  VALID_FROM_TOO_EARLY: z.object({ earliest: z.string() }),
  CALC_ERRORS: z.object({
    days: z.array(z.object({ personId: z.string(), date: z.string(), code: z.string() })).min(1),
  }),
  PLAN_STALE: z.object({
    reason: z.enum(['draft', 'version', 'date', 'build', 'goldens', 'expired']),
  }),
  // 006: состояние агента FR-008 — какую полосу показать вместо разговора.
  AGENT_NOT_READY: z.object({
    state: z.enum([
      'ready',
      'not_installed',
      'outdated',
      'logged_out',
      'login_expired',
      'not_linked',
      'unknown',
    ]),
  }),
  // 007: шаги площадки, которые ещё не готовы (действие закрытого шага, запуск).
  STEPS_NOT_READY: z.object({ steps: z.array(z.number().int().min(1).max(10)).min(1) }),
} as const satisfies Partial<Record<ResponseErrorCode, z.ZodType>>;

type DetailsTable = typeof ERROR_DETAILS;
/** Коды ответа с `details`. */
export type DetailedErrorCode = keyof DetailsTable;
/** `details` кода: у кода без `details` — `never`. */
export type ErrorDetails<C extends ResponseErrorCode> = C extends DetailedErrorCode
  ? z.infer<DetailsTable[C]>
  : never;

const DETAILED_CODES = Object.keys(ERROR_DETAILS) as DetailedErrorCode[];
const PLAIN_CODES = RESPONSE_ERROR_CODES.filter((c) => !(DETAILED_CODES as string[]).includes(c)) as [
  Exclude<ResponseErrorCode, DetailedErrorCode>,
  ...Exclude<ResponseErrorCode, DetailedErrorCode>[],
];

const head = { message: z.string(), requestId: z.string() };
const detailed = <C extends DetailedErrorCode>(code: C) =>
  z.strictObject({ code: z.literal(code), ...head, details: ERROR_DETAILS[code] });

/**
 * Тело ошибки `{ error: { code, message, requestId, details? } }` — формат 001 с `details` по коду (Р-91).
 * Других полей в `error` нет: `retryAfterSec` — только в `details`.
 */
export const ErrorBody = z.object({
  error: z.discriminatedUnion('code', [
    z.strictObject({ code: z.enum(PLAIN_CODES), ...head }),
    detailed('FORBIDDEN'),
    detailed('DRAFT_CHANGED'),
    detailed('VALIDATION_FAILED'),
    detailed('SIGN_BLOCKED'),
    detailed('RATE_LIMITED'),
    detailed('LOGIN_LOCKED'),
    detailed('STALE_STATE'),
    detailed('QUESTIONS_OPEN'),
    detailed('PERIOD_OVERLAP'),
    detailed('DRAFT_LED_BY_OTHER'),
    detailed('RISK_NOT_ACKNOWLEDGED'),
    detailed('VALID_FROM_TOO_EARLY'),
    detailed('CALC_ERRORS'),
    detailed('PLAN_STALE'),
    detailed('AGENT_NOT_READY'),
    detailed('STEPS_NOT_READY'),
  ]),
});
export type ErrorBody = z.infer<typeof ErrorBody>;
